import React, { useState, useRef, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { uploadToR2, deleteFromR2 } from '../../services/storageService';
import CropModal from './CropModal';

const { FiUploadCloud, FiLoader, FiAlertCircle, FiMaximize, FiTrash2 } = FiIcons;

const ImageUpload = ({ 
  onUploadComplete, 
  onStatusChange, 
  path, 
  label, 
  currentImage, 
  aspect = 1, 
  idealSize = "800x800",
  height = "h-64",
  hideHeader = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(currentImage || null);
  const [tempImage, setTempImage] = useState(null);
  const lastUploadedUrlRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (onStatusChange) onStatusChange(uploading);
  }, [uploading, onStatusChange]);

  useEffect(() => {
    if (currentImage !== preview && currentImage !== lastUploadedUrlRef.current) {
      setPreview(currentImage);
    }
  }, [currentImage]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Only JPG, PNG, WEBP, and GIF are allowed.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setTempImage(reader.result);
      setError(null);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    const confirmDelete = window.confirm("Permanently delete this image from the cloud?");
    if (!confirmDelete) return;
    setUploading(true);
    try {
      if (preview) await deleteFromR2(preview);
      lastUploadedUrlRef.current = '';
      setPreview(null);
      onUploadComplete('');
    } catch (err) {
      setError("Deletion failed.");
    } finally {
      setUploading(false);
    }
  };

  const handleCropComplete = async (croppedFile) => {
    const oldImage = preview;
    setTempImage(null);
    setUploading(true);
    setError(null);
    try {
      const publicUrl = await uploadToR2(croppedFile, path);
      if (publicUrl) {
        lastUploadedUrlRef.current = publicUrl;
        if (oldImage && oldImage !== publicUrl && !oldImage.startsWith('data:')) {
          await deleteFromR2(oldImage);
        }
        setPreview(publicUrl);
        onUploadComplete(publicUrl);
      } else {
        throw new Error("Upload failed.");
      }
    } catch (err) {
      setError(err.message || 'Upload failed.');
      setPreview(oldImage);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="w-full h-full flex flex-col">
      {!hideHeader && (
        <div className="flex justify-between items-end px-1 mb-4">
          {label && <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</label>}
          <span className="text-[9px] font-black text-indigo-400 uppercase tracking-tighter bg-indigo-50 px-2 py-0.5 rounded-md">Ideal: {idealSize}</span>
        </div>
      )}
      
      <div 
        onClick={() => !uploading && fileInputRef.current?.click()} 
        className={`relative ${height} w-full rounded-[2.5rem] border-2 border-dashed transition-all cursor-pointer overflow-hidden flex flex-col items-center justify-center group ${preview ? 'border-indigo-500' : 'border-slate-200 hover:border-indigo-400 bg-slate-50'}`}
      >
        {preview ? (
          <>
            <img src={preview} alt="Preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-slate-900/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center backdrop-blur-[2px] gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center border border-white/30">
                <SafeIcon icon={FiMaximize} className="text-white text-xl" />
              </div>
              <button type="button" onClick={handleRemove} className="w-14 h-14 bg-red-500/80 backdrop-blur-md rounded-2xl flex items-center justify-center border border-red-400/30 hover:bg-red-600 transition-all">
                <SafeIcon icon={FiTrash2} className="text-white text-xl" />
              </button>
            </div>
          </>
        ) : (
          <div className="text-center p-4">
            <div className="w-12 h-12 bg-white rounded-2xl shadow-sm flex items-center justify-center mx-auto mb-3 border border-slate-100 group-hover:scale-110 transition-transform">
              <SafeIcon icon={FiUploadCloud} className="text-slate-400 text-xl" />
            </div>
            <p className="text-[10px] font-black text-slate-900 uppercase tracking-widest">Select & Crop</p>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white/90 backdrop-blur-md flex flex-col items-center justify-center z-10">
            <SafeIcon icon={FiLoader} className="text-3xl text-indigo-600 animate-spin mb-2" />
            <span className="text-[8px] font-black text-indigo-900 uppercase tracking-[0.2em]">Syncing...</span>
          </div>
        )}

        {error && (
          <div className="absolute bottom-2 inset-x-2 bg-red-600 text-white text-[8px] py-2 px-3 rounded-xl flex items-center justify-center font-black uppercase tracking-widest shadow-lg z-20">
            <SafeIcon icon={FiAlertCircle} className="mr-1" /> {error}
          </div>
        )}
      </div>
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" accept="image/*" />
      {tempImage && (
        <CropModal image={tempImage} aspect={aspect} onCropComplete={handleCropComplete} onCancel={() => setTempImage(null)} />
      )}
    </div>
  );
};

export default ImageUpload;